import styled from 'styled-components';
import { colors } from 'constants/colors';
import ReactTooltip from 'react-tooltip';

import { GetSupplyOrdersQuery, SupplyOrderProductStockVariationFragmentFragment } from 'data/__generated__';

import { getDayLabels } from './utils';

export const ProductsInfoStockVariationTable = ({
    isComputed,
    orderingTimes,
    productStockVariation,
    startDate,
    precalculatedDayLabels,
    shouldDisplayTooltip, // if this component is being mapped, too many tooltips (should only show tooltip for 1-3 components) will freeze the browser tab
    timeWindow = null,
}: {
    isComputed: boolean;
    orderingTimes?: GetSupplyOrdersQuery['supplyOrders'][number]['supplier']['orderingTimes'];
    productStockVariation: SupplyOrderProductStockVariationFragmentFragment;
    startDate?: string | null;
    precalculatedDayLabels?: { label: string; orderDayLabels: string[] }[];
    shouldDisplayTooltip: boolean;
    timeWindow?: number | null;
}) => {
    const {
        outboundStockVariation,
        productId,
        productSwitchDetails,
        missingStockBetweenDeliveries,
        stockVariation,
        stockVariationFromSupplyOrders,
        forecasts: { forecastsStoreDry },
        computedStockThreshold,
    } = productStockVariation;

    const timeWindowToUse = timeWindow ?? forecastsStoreDry.length;

    const dayLabels =
        precalculatedDayLabels ??
        getDayLabels({
            isComputed: isComputed ?? false,
            startDate,
            timeWindow: timeWindowToUse,
            orderingTimes,
        });

    return (
        <Container>
            <tbody>
                <tr>
                    {dayLabels.slice(0, timeWindowToUse).map(({ label, orderDayLabels }, index: number) => {
                        return (
                            <DaysTableCell
                                key={index}
                                $canBeDelivered={!!orderDayLabels.length}
                                data-for={`stock-variation-${productId}-${index}`}
                                data-tip={`Livré ce jour-là si commandé: ${orderDayLabels.join(' ou ')}`}
                            >
                                {label}
                                {shouldDisplayTooltip ? (
                                    <ReactTooltip
                                        id={`stock-variation-${productId}-${index}`}
                                        disable={!orderDayLabels.length}
                                    />
                                ) : null}
                            </DaysTableCell>
                        );
                    })}
                </tr>
                <tr>
                    {outboundStockVariation.slice(0, timeWindowToUse).map((stockDelta: number, index: number) => {
                        return (
                            <ProductsInfoStockDeltaTableCell key={index} color={colors.red}>
                                {stockDelta ? stockDelta : ''}
                            </ProductsInfoStockDeltaTableCell>
                        );
                    })}
                </tr>
                <tr>
                    {productSwitchDetails.variations
                        .slice(0, timeWindowToUse)
                        .map((stockDelta: number, index: number) => {
                            return (
                                <ProductsInfoStockDeltaTableCell key={index} color={colors.violet}>
                                    {stockDelta ? stockDelta : ''}
                                </ProductsInfoStockDeltaTableCell>
                            );
                        })}
                </tr>
                <tr>
                    <ProductsInfoStockDeltaTableCell color={colors.orange}>
                        {missingStockBetweenDeliveries ? `-${missingStockBetweenDeliveries}` : ''}
                    </ProductsInfoStockDeltaTableCell>
                </tr>
                <tr>
                    {stockVariationFromSupplyOrders
                        .slice(0, timeWindowToUse)
                        .map((stockDelta: number, index: number) => {
                            return (
                                <ProductsInfoStockDeltaTableCell key={index} color={colors.blue}>
                                    {stockDelta ? `+${stockDelta}` : ''}
                                </ProductsInfoStockDeltaTableCell>
                            );
                        })}
                </tr>
                <tr>
                    {stockVariation.slice(0, timeWindowToUse).map((stockVariationValue: number, index: number) => {
                        return (
                            <ProductsInfoStockVariationTableCell
                                key={index}
                                $shortageIfOrderedToday={stockVariationValue < 0}
                                $underStockThresholdIfOrderedToday={stockVariationValue < computedStockThreshold}
                            >
                                {stockVariationValue}
                            </ProductsInfoStockVariationTableCell>
                        );
                    })}
                </tr>
            </tbody>
        </Container>
    );
};

const Container = styled.table`
    border-collapse: collapse;
`;

const DaysTableCell = styled.td<{ $canBeDelivered?: boolean }>`
    font-size: 12px;
    text-align: center;
    padding: 0 2px;
    color: ${({ theme, $canBeDelivered }) => ($canBeDelivered ? theme.textColor : theme.infoTextColor)};
`;

const ProductsInfoStockVariationTableCell = styled.td<{
    $shortageIfOrderedToday?: boolean;
    $underStockThresholdIfOrderedToday?: boolean;
}>`
    font-size: 12px;
    text-align: center;
    padding: 0 2px;
    color: ${({ theme, $shortageIfOrderedToday, $underStockThresholdIfOrderedToday }) =>
        $shortageIfOrderedToday
            ? theme.errorColor
            : $underStockThresholdIfOrderedToday
              ? theme.warningColor
              : theme.infoTextColor};
`;

const ProductsInfoStockDeltaTableCell = styled.td`
    text-align: center;
    font-size: 10px;
    color: ${({ color }) => color};
`;
